<script setup>
import { cloneVNode, computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';

import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  priority: {
    type: String,
    default: '',
  },
  showEmpty: {
    type: Boolean,
    default: false,
  },
  iconSizeClass: {
    type: String,
    default: 'size-[18px]',
  },
});
const { t } = useI18n();

const createPriorityBarsIcon = activeCount =>
  h(
    'svg',
    {
      xmlns: 'http://www.w3.org/2000/svg',
      viewBox: '0 0 24 24',
      fill: 'currentColor',
      'aria-hidden': 'true',
    },
    Array.from({ length: 3 }, (_, index) => {
      const height = 8 + index * 6;

      return h('rect', {
        key: index,
        x: 2 + index * 7,
        y: 22 - height,
        width: 5,
        height,
        rx: 1,
        fill: index < activeCount ? 'currentColor' : '#cbd5e1',
      });
    })
  );

const PRIORITY_BAR_ICONS = {
  [CONVERSATION_PRIORITY.HIGH]: createPriorityBarsIcon(3),
  [CONVERSATION_PRIORITY.MEDIUM]: createPriorityBarsIcon(2),
  [CONVERSATION_PRIORITY.LOW]: createPriorityBarsIcon(1),
};
const PRIORITY_ICON = PRIORITY_BAR_ICONS[CONVERSATION_PRIORITY.HIGH];
const URGENT_ICON = h(
  'svg',
  {
    xmlns: 'http://www.w3.org/2000/svg',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': 2,
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    'aria-hidden': 'true',
  },
  [
    h('rect', {
      x: 3,
      y: 3,
      width: 18,
      height: 18,
      rx: 5,
      fill: 'currentColor',
    }),
    h('path', { d: 'M12 8v5', stroke: '#fff' }),
    h('path', { d: 'M12 16h.01', stroke: '#fff' }),
  ]
);

const colors = {
  [CONVERSATION_PRIORITY.URGENT]: 'text-[#dc2626] dark:text-[#f87171]',
  [CONVERSATION_PRIORITY.HIGH]: 'text-[#ea580c] dark:text-[#f97316]',
  [CONVERSATION_PRIORITY.MEDIUM]: 'text-[#f59e0b] dark:text-[#fbbf24]',
  [CONVERSATION_PRIORITY.LOW]: 'text-[#475569] dark:text-[#94a3b8]',
};

const priorityLabels = {
  [CONVERSATION_PRIORITY.URGENT]: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  [CONVERSATION_PRIORITY.HIGH]: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  [CONVERSATION_PRIORITY.MEDIUM]: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  [CONVERSATION_PRIORITY.LOW]: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};

const priorityColors = {
  [CONVERSATION_PRIORITY.URGENT]: { light: '#dc2626', dark: '#f87171' },
  [CONVERSATION_PRIORITY.HIGH]: { light: '#ea580c', dark: '#f97316' },
  [CONVERSATION_PRIORITY.MEDIUM]: { light: '#f59e0b', dark: '#fbbf24' },
  [CONVERSATION_PRIORITY.LOW]: { light: '#475569', dark: '#94a3b8' },
};

const iconColor = computed(() => {
  if (props.priority && colors[props.priority]) {
    return colors[props.priority];
  }
  return 'text-n-slate-5';
});

const iconName = computed(() => {
  let icon = '';

  if (props.priority && colors[props.priority]) {
    icon =
      props.priority === CONVERSATION_PRIORITY.URGENT
        ? URGENT_ICON
        : PRIORITY_BAR_ICONS[props.priority];
  } else if (props.showEmpty) {
    icon = PRIORITY_ICON;
  }

  if (!icon) return '';

  const priorityColor = priorityColors[props.priority];
  const isDarkMode = document.documentElement.classList.contains('dark');

  return cloneVNode(icon, {
    class: [props.iconSizeClass, iconColor.value],
    style: priorityColor
      ? { color: isDarkMode ? priorityColor.dark : priorityColor.light }
      : undefined,
  });
});

const tooltipContent = computed(() => {
  let label = '';

  if (props.priority && priorityLabels[props.priority]) {
    label = t(priorityLabels[props.priority]);
  } else if (props.showEmpty) {
    label = t('CONVERSATION.PRIORITY.OPTIONS.NONE');
  }

  return label ? `${t('CONVERSATION.PRIORITY.TITLE')}: ${label}` : '';
});
</script>

<template>
  <Icon
    v-tooltip.top="{
      content: tooltipContent,
      delay: { show: 500, hide: 0 },
    }"
    :icon="iconName"
  />
</template>
